import { useEffect, useState } from "react";
import { ArcElement, Chart as ChartJS, Tooltip } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import { useLazyGetDistrictRevenueQuery } from "../../../../../../service/transaction-service";
import { DistrictRevenueItem } from "../../../../../../model/transaction";
import { Money } from "../../../../../../model/common/money";
import { getErrorMessage } from "../../../../../../utils/error-handler";
import { nextDayStart, startOfDay } from "../../../../../../utils/date-params";
import styles from "./DistrictRevenue.module.scss";

ChartJS.register(ArcElement, Tooltip);

const DISTRICT_COLORS = [
	"#1a0a2e",
	"#2d1057",
	"#3d1a78",
	"#5a259c",
	"#7030b8",
	"#8c44ce",
	"#a55cdf",
	"#bc78ea",
	"#cf96f0",
	"#ddb0f5",
	"#e8c8f8",
	"#f0d9fb",
	"#f6e8fd",
];

const formatCurrency = (value: number) => new Money("NGN", value).toLocaleString();

interface DistrictRevenueProps {
	dateFrom: Date | null;
	dateTo: Date | null;
	onLoadingChange?: (loading: boolean) => void;
}

export const DistrictRevenue = ({ dateFrom, dateTo, onLoadingChange }: DistrictRevenueProps) => {
	const [hoveredDistrict, setHoveredDistrict] = useState<DistrictRevenueItem | null>(null);
	const [districts, setDistricts] = useState<DistrictRevenueItem[]>([]);
	const [totalAmount, setTotalAmount] = useState<number>(0);

	const [getDistrictRevenue, { isFetching }] = useLazyGetDistrictRevenueQuery();

	useEffect(() => {
		onLoadingChange?.(isFetching);
	}, [isFetching, onLoadingChange]);

	useEffect(() => {
		getDistrictRevenue({
			...(dateFrom && { DateFrom: startOfDay(dateFrom) }),
			...(dateTo && { DateTo: nextDayStart(dateTo) }),
		})
			.unwrap()
			.then(data => {
				setDistricts(data.districts);
				setTotalAmount(data.totalAmount);
			})
			.catch(err => getErrorMessage(err.data));
	}, [dateFrom, dateTo, getDistrictRevenue]);

	const chartData = {
		labels: districts.map(d => d.district),
		datasets: [
			{
				data: districts.map(d => d.amount),
				backgroundColor: districts.map((_, i) => DISTRICT_COLORS[i % DISTRICT_COLORS.length]),
				borderWidth: 0,
				hoverOffset: 6,
			},
		],
	};

	const chartOptions = {
		cutout: "72%",
		plugins: {
			legend: { display: false },
			tooltip: {
				callbacks: {
					label: (ctx: any) => ` ${formatCurrency(ctx.raw)}`,
				},
			},
		},
		onHover: (_: any, elements: any[]) => {
			if (elements.length > 0) {
				setHoveredDistrict(districts[elements[0].index] ?? null);
			} else {
				setHoveredDistrict(null);
			}
		},
	};

	if (districts.length === 0 && !isFetching) {
		return <div className={styles.empty}>No district revenue data for the selected period.</div>;
	}

	return (
		<div className={styles.content}>
			<div className={styles.chartWrapper}>
				<Doughnut data={chartData} options={chartOptions as any} />
				<div className={styles.chartCenter}>
						<>
							<span className={styles.chartCenterLabel}>Total</span>
							<span className={styles.chartCenterValue}>{formatCurrency(totalAmount)}</span>
						</>
				</div>
			</div>

			<div className={styles.leaderboard}>
				{districts.map((item, i) => (
					<div key={item.district} className={styles.leaderboardRow}>
						<div className={styles.leaderboardLeft}>
							<span
								className={styles.dot}
								style={{ background: DISTRICT_COLORS[i % DISTRICT_COLORS.length] }}
							/>
							<span className={styles.districtName}>{item.district}</span>
						</div>
						<div className={styles.leaderboardRight}>
							<span className={styles.amount}>{formatCurrency(item.amount)}</span>
							<span className={styles.badge}>{item.percentage.toFixed(2)}%</span>
						</div>
					</div>
				))}
			</div>
		</div>
	);
};
